'use client';

import { useEffect, useMemo, useRef } from 'react';
import type { NavItem } from '@/components/layout/header/Header.types';
import type { FooterLink } from '@/components/layout/footer/Footer.types';
import { STATIC_FOOTER_LINKS } from '@/lib/constants/NavItems';
import {
  resolveWhiteLabelNav,
  type WhiteLabelNavEntry,
  type WhiteLabelNavTarget,
} from '@/lib/white-label-nav';
import { getCompanyBookingEnablement } from '@/lib/theme-utils';
import { logger } from '@/lib/logger';
import { useThemeStore } from '@/stores/theme.store';
import { useAuthStore } from '@/stores/user.store';
import { useContactUsStore } from '@/stores/contact-us.store';
import { useSupportWidget } from '@/hooks/useSupportWidget';
import { useParkingValidationVisible } from '@/hooks/parking-validation/useParkingValidationVisible';

export interface WhiteLabelNavItems {
  /** Header nav entries in back-office order (the layout appends guest Sign In). */
  headerNavItems: NavItem[];
  /** White-label footer entries (About Us, Contact Us, Help) followed by the static links. */
  footerLinks: FooterLink[];
}

/** data-cy for header entries — built-ins keep the selectors the QA suite already uses. */
const headerDataCy = (entry: WhiteLabelNavEntry): string => {
  if (entry.key === 'bookParking') return 'header-primary-nav-link';
  if (entry.key === 'custom') return 'header-custom-nav-link';
  return 'header-secondary-nav-link';
};

/**
 * Header and footer navigation driven by the company's back-office white-label tabs,
 * replicating the legacy reservation-site header. The rules live in
 * {@link resolveWhiteLabelNav}; this hook supplies the store inputs and turns each
 * entry's target into an href or click handler.
 */
export function useWhiteLabelNav(): WhiteLabelNavItems {
  const theme = useThemeStore((s) => s.theme);
  const user = useAuthStore((s) => s.user);
  const contactUs = useContactUsStore((s) => s.contactUs);
  const showParkingValidation = useParkingValidationVisible();

  // The widget helpers are rebuilt every render; read the latest through a ref so the
  // memoised items below keep a stable click handler.
  const supportWidget = useSupportWidget();
  const supportWidgetRef = useRef(supportWidget);
  useEffect(() => {
    supportWidgetRef.current = supportWidget;
  });

  const isLoggedIn = !!user;

  return useMemo(() => {
    const nav = resolveWhiteLabelNav(theme?.whiteLabelTabs, {
      isLoggedIn,
      showParkingValidation,
      hourlyEnabled: getCompanyBookingEnablement(theme).hourly,
      hasContactWidget: !!contactUs?.widgetId,
    });

    const openSupportWidget = () => {
      const { isWidgetReady, openWidget } = supportWidgetRef.current;
      if (!isWidgetReady) {
        logger.warn('Help clicked but no support widget is loaded', {
          feature: 'white-label-nav',
          route: 'help',
        });
        return;
      }
      openWidget();
    };

    const toLink = (target: WhiteLabelNavTarget): Pick<NavItem, 'href' | 'onClick'> => {
      switch (target.kind) {
        case 'route':
          return { href: target.href };
        case 'supportWidget':
          return { onClick: openSupportWidget };
        case 'redirect':
          // App paths go through the base-path-aware Link; absolute URLs replace the
          // page, as legacy did with window.open(url, '_self').
          return target.url.startsWith('/')
            ? { href: target.url }
            : { onClick: () => window.location.assign(target.url) };
      }
    };

    const headerNavItems: NavItem[] = nav.header.map((entry) => ({
      label: entry.title,
      labelKey: entry.key === 'custom' ? '' : entry.key,
      displayLabel: entry.key === 'custom' ? entry.title : undefined,
      dataCy: headerDataCy(entry),
      ...toLink(entry.target),
    }));

    const footerLinks: FooterLink[] = [
      ...nav.footer.map((entry) => ({
        label: entry.title,
        labelKey: entry.key,
        ...toLink(entry.target),
      })),
      ...STATIC_FOOTER_LINKS,
    ];

    return { headerNavItems, footerLinks };
  }, [theme, isLoggedIn, showParkingValidation, contactUs?.widgetId]);
}
